import React, { useState } from "react";
import "./Home.css"; // Reusing the achievements styles from Home.css

const ImagePopup: React.FC<{ image: string; alt: string; isOpen: boolean; onClose: () => void }> = ({
  image,
  alt,
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="image-popup-overlay" onClick={onClose}>
      <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="image-popup-close" onClick={onClose} aria-label="Close popup">
          ×
        </button>
        <img src={image} alt={alt} className="image-popup-image" />
      </div>
    </div>
  );
};

const Achievements: React.FC = () => {
  const [popupImage, setPopupImage] = useState<string | null>(null);
  const [popupAlt, setPopupAlt] = useState<string>("");

  const openImagePopup = (image: string, alt: string) => {
    setPopupImage(image);
    setPopupAlt(alt);
  };

  const closeImagePopup = () => {
    setPopupImage(null);
    setPopupAlt("");
  };

  return (
    <>
      <div className="achievements-page">
        {/* Hero Section */}
        <section className="achievements-hero">
          <div className="container">
            <div className="achievements-hero-content">
              <h1 className="achievements-hero-title">
                <span className="highlight">Hackathon</span> Victories
              </h1>
              <p className="achievements-hero-description">
                Throughout my career, I've had the privilege of winning several prestigious hackathons,
                showcasing innovative solutions to real-world problems and demonstrating technical excellence
                in competitive environments.
              </p>
              <div className="achievements-stats">
                <div className="stat-item">
                  <span className="stat-number">4</span>
                  <span className="stat-label">Hackathons Won</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Teams Competed</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">3</span>
                  <span className="stat-label">Cities</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements Grid */}
        <section className="achievements-section">
          <div className="container">
            <div className="achievements-grid">
              {/* Nagarjuna College Hackathon */}
              <div className="achievement-card">
                <div className="achievement-content">
                  <div className="achievement-badge">🏆 1st Place</div>
                  <h3 className="achievement-title">Nagarjuna College Hackathon</h3>
                  <p className="achievement-description">
                    I was the sole team member and emerged victorious in my college hackathon.
                    I built the <strong>Renit Platform</strong> - an innovative rental marketplace where users
                    can rent anything based on their custom rental price, solving real community needs.
                  </p>
                  <div className="achievement-highlight">
                    <span className="highlight-label">Achievement:</span> Solo Winner
                  </div>
                 
                </div>
                <div className="achievement-image">
                  <img
                    src="/achievements/won_at_nagarjuna.jpg"
                    alt="Nagarjuna College Hackathon Victory"
                    onClick={() =>
                      openImagePopup(
                        "/achievements/won_at_nagarjuna.jpg",
                        "Nagarjuna College Hackathon Victory"
                      )
                    }
                  />
                </div>
              </div>

              {/* SecurityPal NepalHacks */}
              <div className="achievement-card">
                <div className="achievement-content">
                  <div className="achievement-badge">🏆 National Winner</div>
                  <h3 className="achievement-title">NepalHacks at SecurityPal</h3>
                  <p className="achievement-description">
                    Competed in a country-wide hackathon where only <strong>10 teams were selected</strong> from
                    <strong>50+ teams</strong> based on idea quality and tech stack. My team won by creating a
                    <strong>Commute-Based Traffic Prediction Platform</strong> with pattern analysis and early alert systems.
                  </p>
                  <div className="achievement-highlight">
                    <span className="highlight-label">Selection Rate:</span> Top 10 from 50+ teams
                  </div>
                   
                </div>
                <div className="achievement-image">
                  <img
                    src="/achievements/won_at_securitypal.jpg"
                    alt="NepalHacks Country Hackathon Victory"
                    onClick={() =>
                      openImagePopup(
                        "/achievements/won_at_securitypal.jpg",
                        "NepalHacks Country Hackathon Victory"
                      )
                    }
                  />
                </div>
              </div>

              {/* KIST All College Hackathon */}
              <div className="achievement-card">
                <div className="achievement-content">
                  <div className="achievement-badge">🏆 College Champion</div>
                  <h3 className="achievement-title">Kathmandu All College Hackathon</h3>
                  <p className="achievement-description">
                    Competed against <strong>36 teams</strong> from colleges across Kathmandu. We developed
                    <strong>HamroClass</strong> - a comprehensive solution addressing the traditional syllabus
                    lag problem that affects colleges throughout Nepal.
                  </p>
                  <div className="achievement-highlight">
                    <span className="highlight-label">Competition:</span> 36 teams from multiple colleges
                  </div>
                  
                </div>
                <div className="achievement-image">
                  <img
                    src="/achievements/won_at_kist.jpg"
                    alt="KIST All College Hackathon Victory"
                    onClick={() =>
                      openImagePopup(
                        "/achievements/won_at_kist.jpg",
                        "KIST All College Hackathon Victory"
                      )
                    }
                  />
                </div>
              </div>

              {/* Province Selection */}
              <div className="achievement-card">
                <div className="achievement-content">
                  <div className="achievement-badge">🏆 Provincial Winner</div>
                  <h3 className="achievement-title">Country-Wide Provincial Selection</h3>
                  <p className="achievement-description">
                    Participated in a nationwide hackathon with participants from multiple provinces across the country.
                    Our team achieved the prestigious selection from the <strong>Capital Province</strong>,
                    representing excellence in innovation and technical implementation.
                  </p>
                  <div className="achievement-highlight">
                    <span className="highlight-label">Recognition:</span> Capital Province Selection
                  </div>
                   
                </div>
                <div className="achievement-image">
                  <img
                    src="/achievements/selected_from_province.jpg"
                    alt="Capital Province Selection"
                    onClick={() =>
                      openImagePopup(
                        "/achievements/selected_from_province.jpg",
                        "Capital Province Selection"
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Image Popup */}
      <ImagePopup
        image={popupImage || ""}
        alt={popupAlt}
        isOpen={!!popupImage}
        onClose={closeImagePopup}
      />
    </>
  );
};

export default Achievements;
